import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

import Container from './Container';
import CloudinaryImage from './CloudinaryImage';

export default function SoftwareDevelopment({ Services }) {
  const [activeDiv, setActiveDiv] = useState(null); // Track the active div
  const divRefs = useRef([]); // Store references to all divs

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveDiv(entry.target.getAttribute('data-id')); // Set the active div ID
          }
        });
      },
      {
        root: null, // Use the viewport as the root
        threshold: 1, // Trigger when 50% of the div is visible
      }
    );

    // Observe each div
    divRefs.current.forEach(div => {
      if (div) observer.observe(div);
    });

    return () => {
      // Unobserve all divs on cleanup
      divRefs.current.forEach(div => {
        if (div) observer.unobserve(div);
      });
    };
  }, []);

  return (
    <Container>
      <div className="grid md:grid-cols-2 grid-cols-1 md:gap-x-16">
        <div className="md:sticky md:top-[150px] h-max md:mb-0 mb-[30px]">
          {Services?.bg_title && (
            <h6 className="lg:text-[34px] text-[24px] font-[800] bg-gradient-to-r from-[#0050D5] to-[#62207E] inline-block text-transparent bg-clip-text">
              {Services?.bg_title}
            </h6>
          )}
          {Services?.title && (
            <h2 className="text-[#0C2139] font-[800] xl:text-[50px] md:text-[32px] text-[25px] leading-tight mb-3">
              {Services?.title}
            </h2>
          )}
          {Services?.description && (
            <p className="text-[#000B1C] font-[400] lg:text-[20px] text-[16px] leading-tight">
              {Services?.description}
            </p>
          )}
          {Services?.descriptionEditor && (
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              className={`text-[#000B1C] font-[400] lg:text-[24px] md:text-[20px] text-[16px] leading-tight`}
              source={Services?.descriptionEditor}
            >
              {Services?.descriptionEditor}
            </ReactMarkdown>
          )}
        </div>
        <div>
          {Services?.reasonList?.map((data, index) => (
            <div
              key={data?.id}
              ref={el => (divRefs.current[index] = el)} // Add reference to div
              data-id={index} // Assign a unique ID to each div
              className={`transition-colors duration-500 ease-in-out rounded-[20px] p-[30px] mb-10 ${
                activeDiv === `${index}`
                  ? 'bg-[linear-gradient(180deg,_#0050D5_0%,_#000000_100%)] text-[#ffffff]'
                  : 'bg-[#F5F8FF] text-[#00102B]'
              }`} // Dynamic background color based on activeDiv
            >
              {data?.image?.data &&
                data?.image?.data?.map(img => (
                  <span key={img?.id} className="block mb-3">
                    <CloudinaryImage
                      backendImgUrl={img?.attributes?.url}
                      className={`w-[70px] h-[70px]`}
                      alt={img?.attributes?.alternativeText}
                      type="icon"
                    />
                  </span>
                ))}
              {data?.headingLink ? (
                <h3>
                  <Link
                    href={`${data?.headingLink}`}
                    className="mb-2 lg:text-[24px] text-[20px] font-[700] block"
                  >
                    {data?.title}
                  </Link>
                </h3>
              ) : (
                <h3 className="mb-2 lg:text-[24px] text-[20px] font-[700] block">
                  {data?.title}
                </h3>
              )}

              {data?.editor && (
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  className={`md:text-[18px] text-[14px] font-[400] leading-tight`}
                  source={data?.editor}
                >
                  {data?.editor}
                </ReactMarkdown>
              )}
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
}
